<template>
  <div id="bubbles-factory">
    <div class="sidebar">
      <div class="research-bar">
        <el-input placeholder="Filter bubble" v-model="searchBubble" class="input-with-select">
          <el-button slot="append" icon="el-icon-search"></el-button>
        </el-input>
      </div>
      <div class="listing" v-bar>
        <draggable :options="{group:'bubbles'}" v-model="bubblesSorted" @start="onDrag=true" @end="onEnd" :move="checkMove">
          <bubble class="margin-right" v-for="bubble in bubblesSorted" :key="bubble.id" :id="bubble.id" align="vertical" @reload="init"></bubble>
        </draggable>
      </div>
    </div>

    <div class="selected">
      <draggable :options="{group:'bubbles'}" v-model="bubblesSelected" @end="onEnd">
        <bubble v-for="(bubble, index) in bubblesSelected" v-bind:key="bubble.id" :id="bubble.id"  align="horizontal" @delete="bubblesSelected.splice(index, 1)" @reload="init"></bubble>
      </draggable>
      <div class="background" v-if="!onDrag && bubblesSelected.length === 0">
        <span><font-awesome-icon icon="file-export"></font-awesome-icon></span>
        <span>Drop some bubbles here to aggregate users (max: 5 bubbles)</span>
      </div>
    </div>

    <div class="results">
      <div id="simulator">
        <div class="venntooltip"></div>
      </div>
      <font-awesome-icon class="loader-cog" v-if="loadingIntersection" icon="cog" spin></font-awesome-icon>
      <el-button class="erase" type="danger" v-if="bubblesSelected.length > 0" @click="eraseBubblesSelected">Erase</el-button>
    </div>
    <aside class="aside">
      <h3 v-if="bubblesIntersections.length !== 0">Intersection</h3>

      <intersection v-for="(intersection, index) in bubblesIntersections" :key="index" :intersection="intersection"></intersection>

      <div class="background" v-if="bubblesIntersections.length === 0">
        <span><font-awesome-icon :icon="['fab', 'connectdevelop']"></font-awesome-icon></span>
        <span>All intersections from your bubbles appear here.</span>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import bubble from '@/components/customers/bubble.vue'
import intersection from '@/components/bubbles/bubble-intersection.vue'
import HTTP from '@rocketfid/api'
import draggable from 'vuedraggable'
import * as d3 from 'd3'
import * as venn from 'venn.js'

export default {
  name: 'bubbles-factory',

  components: { draggable, bubble, intersection },

  data () {
    return {
      searchBubble: null,
      bubblesSelected: [],
      bubblesIntersections: [],
      onDrag: false,

      loadingIntersection: false
    }
  },
  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    }),

    ...mapGetters('bubbles', {
      getById: 'getById'
    }),

    ...mapState('bubbles', {
      bubbles: 'items'
    }),

    bubblesSorted: {
      get: function () {
        let bubbles = this.bubbles

        bubbles = bubbles.filter((bubble) => {
          return this.bubblesSelected.indexOf(bubble) === -1
        })

        bubbles = bubbles.slice().sort(function (a, b) {
          return new Date(b.dateupdate) - new Date(a.dateupdate)
        })

        // search on name
        if (this.searchBubble) {
          bubbles = bubbles.filter(item => {
            let find = false
            if (item.tags.length !== 0) {
              for (let i = 0; i < item.tags.length; i++) {
                if (item.tags[i].value) {
                  find = item.tags[i].value.toLowerCase().indexOf(this.searchBubble.toLowerCase()) > -1 ? true : find
                }
              }

              /* item.tags.forEach((tag) => {
                console.log(tag)
                find = tag.toLowerCase().indexOf(this.searchBubble.toLowerCase()) > -1 ? true : find
              }) */
            }

            return item.name.toLowerCase().indexOf(this.searchBubble.toLowerCase()) > -1 || find
          })
        }

        return bubbles
      },

      set: function (newValue) {

      }
    }
  },
  watch: {
    site: {
      handler: 'init',
      immediate: true
    },

    bubblesSelected: {
      handler: 'onEnd',
      deep: true
    }
  },
  methods: {
    ...mapActions('bubbles', ['loadBubbles', 'addBubbleFromIntersection']),

    init () {
      if (this.site) {
        this.loadBubbles()
      }
    },
    checkMove () {
      if (this.bubblesSelected.length > 4) return false
    },

    onEnd () {
      this.onDrag = false
      if (this.bubblesSelected.length > 0) {
        this.loadingIntersection = true
        HTTP
          .setInstance(this.site.sitekey)
          .bubble.getIntersections(this.bubblesSelected)
          .then((results) => {
            this.compute(results)
            this.loadingIntersection = false
          })
      } else {
        d3.select('#simulator > *').remove()
      }
    },

    compute (results) {
      this.bubblesIntersections = []
      this.bubblesIntersections.push(...results.filter((inter) => {
        return inter.intersectioncount > 0
      }))

      let globalSets = []

      // 1 - Create bubbles without interaction
      this.bubblesSelected.forEach((bubble) => {
        let set = {sets: [bubble.id], label: bubble.name, size: bubble.usercount, color: bubble.color}
        globalSets.push(set)
      })

      // 2 - Create intersection
      results.forEach((intersection) => {
        let set = {}
        set.sets = []

        intersection.bubbles.forEach((bubble) => {
          set.sets.push(bubble.id)
        })

        set.size = intersection.users.length
        globalSets.push(set)
      })

      // 3 - Display the Venn Diagram
      let chart = venn.VennDiagram()

      let div = d3.select('#simulator')

      div.datum(globalSets).call(chart)

      globalSets.forEach((set) => {
        let c = d3.select("svg g[data-venn-sets='" + set.sets[0] + "']")
        if (c && set.color) {
          c.select('path').style('fill', set.color)
        }
      })

      let tooltip = d3.select('#simulator div.venntooltip')

      div.selectAll('path')
        .style('stroke-opacity', 0)
        .style('stroke', '#fff')
        .style('stroke-width', 3)

      div.selectAll('g')
        .on('mouseover', function (d, i) {
          // sort all the areas relative to the current item
          venn.sortAreas(div, d)

          // Display a tooltip with the current size
          tooltip.transition().duration(400).style('opacity', 0.9)
          tooltip.text(d.size + ' users')

          // highlight the current path
          let selection = d3.select(this).transition('tooltip').duration(400)
          selection.select('path')
            .style('fill-opacity', d.sets.length === 1 ? 0.4 : 0.1)
            .style('stroke-opacity', 1)
        })

        .on('mousemove', function () {
          tooltip.style('left', (d3.event.pageX) + 'px')
            .style('top', (d3.event.pageY - 28) + 'px')
        })

        .on('mouseout', function (d, i) {
          tooltip.transition().duration(400).style('opacity', 0)
          let selection = d3.select(this).transition('tooltip').duration(400)
          selection.select('path')
            .style('fill-opacity', d.sets.length === 1 ? 0.25 : 0.0)
            .style('stroke-opacity', 0)
        })
    },

    mouseOverIntersection (intersection) {
      let id = null
      intersection.bubbles.forEach((bubble) => {
        if (!id) {
          id = bubble.id
        } else {
          id += '_' + bubble.id
        }
      })
      d3.select("svg g[data-venn-sets='" + id + "']").select('path')
        .style('fill-opacity', 0.25)
        .style('stroke-opacity', 1)
    },

    mouseLeaveIntersection (intersection) {
      let id = null
      intersection.bubbles.forEach((bubble) => {
        if (!id) {
          id = bubble.id
        } else {
          id += '_' + bubble.id
        }
      })
      d3.select("svg g[data-venn-sets='" + id + "']").select('path')
        .style('fill-opacity', 0)
        .style('stroke-opacity', 0)
    },

    eraseBubblesSelected () {
      this.bubblesSelected = []
      this.bubblesIntersections = []
      this.onEnd()
    }
  }
}

</script>

<style lang="scss" >
#bubbles-factory {
    margin-top: -24px;
    border-bottom:1px solid #eaeaea;
    display: grid;
    grid-template-columns: 300px 1fr 300px;
    grid-template-rows: 100px 1fr;
    grid-column-gap: 12px;
    grid-row-gap: 4px;
    grid-column-gap: 0;
    grid-template-areas:
        "sidebar selected aside"
        "sidebar results aside"
        ;

    .sidebar {
        grid-area: sidebar;
        height: 600px;
        overflow-y: hidden;

        .listing {
          height: 500px;
          overflow-y: scroll;
          border-right:1px solid #eaeaea;
          .margin-right {
            margin-right:12px;
          }
        }
    }
    .aside {
        grid-area: aside;
        border-left:1px solid #eaeaea;
        h3 {
          padding: 12px;
          box-sizing: border-box;
        }
    }
    .selected {
        grid-area: selected;
        background: transparent;
        background: #fafafa;
        border-bottom:1px solid #eaeaea;
        height: 100px;
        position: relative;
        > div {
          display: flex !important;
          z-index: 100;
          height: 100%;
          > div {
            flex: 1;
          }
        }
        .background {
          z-index: 1!important;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          padding: 20px;
          box-sizing: border-box;
          display: grid !important;
          grid-template-columns: 1fr;
          grid-template-rows: 1fr 1fr;
          align-items: center;
          text-align: center;

          > span:first-child {
            font-size: 30px;
          }
        }
    }
    .results {
        grid-area: results;
    }
}

.venntooltip {
  position: fixed;
  padding: 8px;
  margin-top:-10px;
  margin-left:10px;
  font-weight: 600;
  color: #565656;
  background: #fff;
  border-radius: 4px;
}

.contextmenu {
  position: fixed;
  top: 0;
  left: 0;
  width: 200px;
  height: 200px;
  background: red;
}

.vb > .vb-dragger {
    z-index: 5;
    width: 12px;
    right: 0;
}

.vb > .vb-dragger > .vb-dragger-styler {
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    -webkit-transform: rotate3d(0,0,0,0);
    transform: rotate3d(0,0,0,0);
    -webkit-transition:
        background-color 100ms ease-out,
        margin 100ms ease-out,
        height 100ms ease-out;
    transition:
        background-color 100ms ease-out,
        margin 100ms ease-out,
        height 100ms ease-out;
    background-color: rgba(48, 121, 244,.1);
    margin: 5px 5px 5px 0;
    border-radius: 20px;
    height: calc(100% - 10px);
    display: block;
}

.vb.vb-scrolling-phantom > .vb-dragger > .vb-dragger-styler {
    background-color: rgba(48, 121, 244,.3);
}

.vb > .vb-dragger:hover > .vb-dragger-styler {
    background-color: rgba(48, 121, 244,.5);
    margin: 0px;
    height: 100%;
}

.vb.vb-dragging > .vb-dragger > .vb-dragger-styler {
    background-color: rgba(48, 121, 244,.5);
    margin: 0px;
    height: 100%;
}

.vb.vb-dragging-phantom > .vb-dragger > .vb-dragger-styler {
    background-color: rgba(48, 121, 244,.5);
}

.research-bar {
  border-bottom:1px solid #eaeaea;
  border-right:1px solid #eaeaea;
  background: #fafafa;
  width: 100%;
  padding: 12px 12px 13px 12px;
  box-sizing: border-box;
}

.intersection {
  box-shadow: 0 0.1rem 0.5rem rgba(0, 0, 0, 0.04);
  align-items: center;
  font-size: 14px;
  padding: 12px 4px;
  margin: 12px;
  display: grid;
  grid-template-columns: 18px 1fr 40px;
  cursor: pointer;

  &:hover {
    .color {
      background: #565656;
    }
  }

  .color {
    height: 100%;
    width: 4px;
    margin-left:6px;
    background: #aaa;
  }

  .name {
    font-weight: 600;
  }
  .actions {
    justify-self: center;
  }
}

#simulator {
  svg {
    margin: 0 auto;
    display: block;
  }
}
.users {
  grid-area: users;
  > span {
    background: #eaeaea;
    color: #565656;
  }
}

aside {
  .background {
    z-index: 1!important;
    width: 100%;
    height: 100%;
    padding: 140px 40px;
    box-sizing: border-box;
    span {
      color: rgb(86,86,86);
      display: block;
      text-align: center;
    }
    > span:first-child {
      font-size: 80px;
      margin-bottom:24px;
    }
  }
}

#bubbles-factory {
  > .results {
    position: relative;

    > .loader-cog {
      font-size: 40px;
      position: absolute;
      top: 20px;
      left: 20px;
      color: #aeaeae;
    }
  }
}

.erase {
  position: absolute;
  top: 12px;
  right: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/bubbles/bubbles-factory.vue